import React from 'react';
import {TapHandlerLogic} from '../../tap-handler/tap-handler.component';
import {OrbitComponentWithState} from "../../base-components/orbit-component-with-state/orbit-component-with-state";
import {Check} from '@sparkware/app-utils';
import {AnyStyledComponent} from 'styled-components';
import styled from 'styled-components';

const DefaultBox = styled.img``;

interface ImageProps {
  box?: AnyStyledComponent
  src: string;
  alt?: string;
  fallbackSrc?: string;
  className?: string;
  title?: string;
  enableTap?: boolean;
  onClick?: (event?: any) => void;
  width?: number | string;
  lazy?: boolean;
}

interface ImageState {
  isFallback: boolean;
  src?: string;
}

const EMPTY_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

export class Image extends OrbitComponentWithState<ImageProps, ImageState> {
    constructor(props: ImageProps) {
        super(props);
        /**
            If this is a lazy load image we initialize the state.src with the fallback image from props
            but in case this is not provided we just set an empty image gif.
            This will prevent the borders to be visible around the image while scrolled into the view.
        */
        this.state = {
            isFallback: false,
            src: this._getInitialSrcState()
        };
    }

    private _getInitialSrcState(): string {
        return this.props.lazy ? (this.props.fallbackSrc || EMPTY_IMAGE) : this.props.src;
    }


    private _getImgTagProps(): React.ImgHTMLAttributes<HTMLImageElement> {
        const result: React.ImgHTMLAttributes<HTMLImageElement> = {};

        if (this.props.className) {
            result.className = this.props.className;
        }

        if (this.props.title) {
            result.title = this.props.title;
        }

        if (!Check.isNullOrUndefined(this.props.width)) {
            result.width = this.props.width;
        }

        result.src = this.props.src;

        if (this.state.src) {
            result.src = this.state.src;
        }


        return result;
    }

    private _setFallbackSrc = (event) => {
        if (!this.props.fallbackSrc) {
            return;
        }


        if (this.state.isFallback) {
            return; // we already tried and failed to set the image to fallback
            // therefore, the fallback image must also be broken, and we should
            // avoid trying to "fix" it again.
        }

        event.persist();

        if(this._isLazyLoadInProgress) {
            /**
                If we reached here it means that the fallback image is broken.
                But if this is a lazy loaded image and the image wasn't scrolled into the view
                then we just set and empty image for the state.src and wait for the image to be scrolled into view
                and give the intersection observer the chance to set the real image src.
            */
            this.setState({isFallback: false, src: EMPTY_IMAGE});
        } else {
            this.setState({isFallback: true, src: this.props.fallbackSrc});
        }
    };


    componentWillUnmount() {
        super.componentWillUnmount();
        this._destroyIntersectionObserver();
        this._destroyTapLogicHandler();
    }

    componentDidUpdate(prevProps: ImageProps, prevState: any, snapshot: any) {
        if(this.props.src !== prevProps.src) {
            if(this._isLazyLoadInProgress) {
                this.setState({
                    src: this._getInitialSrcState()
                });
            } else {
                this.setState({
                    src: this.props.src
                });
            }

        }
    }

    private _tapHandlerLogic: TapHandlerLogic | null = null;
    private _createTapLogicHandler(element: HTMLImageElement) {
        if (this.props.enableTap) {
            this._destroyTapLogicHandler();
            this._tapHandlerLogic = new TapHandlerLogic(element);
        }
    }

    private _destroyTapLogicHandler() {
        if (this._tapHandlerLogic) {
            this._tapHandlerLogic.dispose();
            this._tapHandlerLogic = null;
        }
    }

    private _intersectionObserver: IntersectionObserver | null;

    private get _isLazyLoadInProgress(): boolean {
        if(this._intersectionObserver) {
            return true;
        }

        return false;
    }

    private _createIntersectionObserver(element: HTMLImageElement) {

        if (!this.props.lazy) {
            return;
        }

        // there is no point to lazy load if no src was provided for this image
        if (!this.props.src) {
            return;
        }

        this._destroyIntersectionObserver(); // just in case this is called twice for this component we want to discard the existing intersection observer and create a new one


        this._intersectionObserver = new IntersectionObserver(this._intersectionObserverCallback, {
            root: this.services.mainScreen.mainScreenElement, //if we don't attach to the correct scroll container (the one that has the scroll bars) then the rootMargin doesn't work correctly
            rootMargin: '250px',
            threshold: 0.1
        });

        this._intersectionObserver.observe(element);
    }

    private _destroyIntersectionObserver() {
        if (this._intersectionObserver) {
            this._intersectionObserver.disconnect();
            this._intersectionObserver = null;
        }
    }

    private _intersectionObserverCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        if (entries[0].intersectionRatio > 0 || entries[0].isIntersecting) {
            this._destroyIntersectionObserver();
            this.setState({isFallback: false, src: this.props.src});
        }
    };

    private _captureElementRef = (element: HTMLImageElement) => {
        if (element) {
            this._createTapLogicHandler(element);
            this._createIntersectionObserver(element);
        }
    };

    render() {

        const Box = this.props.box || DefaultBox;

        return (<Box {...this._getImgTagProps()}
                     alt = {this.props.alt || '' /*alt is set here to avoid a react warning which says that all images must have an alt property*/}
                     ref={this._captureElementRef}
                     onError={this._setFallbackSrc}
                     onClick={this.props.onClick}
        />);
    }
}
